import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, Menu, X } from 'lucide-react';

const LOGO = 'https://customer-assets.emergentagent.com/job_blue-well-crm/artifacts/vyxcu57o_image.png';

const NAV = [
  { id: 'services', label: 'Services' },
  { id: 'service-area', label: 'Service Area' },
  { id: 'gallery', label: 'Gallery' },
  { id: 'contact', label: 'Contact' },
];

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

export const PublicHeader = () => {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState('');

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const ids = [...NAV.map((n) => n.id), 'book'];
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(e.target.id);
        });
      },
      { rootMargin: '-45% 0px -50% 0px' }
    );
    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollTo(id);
  };

  return (
    <header
      data-testid="public-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,backdrop-filter,border-color,box-shadow] duration-500 ${
        scrolled ? 'glass !rounded-none border-x-0 border-t-0' : 'bg-transparent border-b border-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[74px] flex items-center justify-between">
        <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="flex items-center gap-3 group" data-testid="public-logo-btn">
          <img src={LOGO} alt="Blue Well Drilling" className="w-10 h-10 object-contain drop-shadow-[0_0_12px_rgba(56,189,248,0.5)] group-hover:scale-105 transition-transform" />
          <span className="text-white font-bold text-lg tracking-tight font-display">
            Blue Well <span className="gradient-text">Drilling</span>
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-9">
          {NAV.map((n) => (
            <button
              key={n.id}
              data-testid={`nav-${n.id}`}
              onClick={() => go(n.id)}
              className={`text-sm font-medium transition-colors duration-200 relative after:absolute after:left-0 after:-bottom-1.5 after:h-px after:bg-[#38BDF8] after:transition-all after:duration-300 ${
                active === n.id ? 'text-[#38BDF8] after:w-full' : 'text-zinc-300 hover:text-white after:w-0 hover:after:w-full'
              }`}
            >
              {n.label}
            </button>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <button
            data-testid="employee-access-link"
            onClick={() => navigate('/login')}
            className="text-xs font-semibold text-zinc-400 hover:text-white transition-colors uppercase tracking-wider"
          >
            Employee Access
          </button>
          <button
            data-testid="header-book-btn"
            onClick={() => go('book')}
            className="cta-shimmer px-5 py-2.5 rounded-full bg-[#1FB6E9] hover:bg-[#38BDF8] text-[#06121f] font-semibold text-sm shadow-[0_8px_28px_-6px_rgba(31,182,233,0.7)] hover:-translate-y-0.5 transition-all duration-200"
          >
            Book Online
          </button>
        </div>

        <button data-testid="mobile-menu-btn" className="lg:hidden text-white p-2" onClick={() => setOpen(!open)} aria-label="Menu">
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden glass !rounded-none px-5 py-5 flex flex-col gap-1" data-testid="mobile-menu">
          {NAV.map((n) => (
            <button key={n.id} onClick={() => go(n.id)} className="text-left py-3 text-zinc-200 font-medium border-b border-white/[0.05]">
              {n.label}
            </button>
          ))}
          <a href="tel:+18636580035" className="flex items-center gap-2 py-3 text-[#38BDF8] font-medium">
            <Phone className="w-4 h-4" /> (863) 658-0035
          </a>
          <div className="flex gap-3 mt-3">
            <button onClick={() => { setOpen(false); navigate('/login'); }} className="flex-1 py-3 rounded-full border border-white/15 text-zinc-200 font-semibold text-sm">
              Employee Access
            </button>
            <button onClick={() => go('book')} className="flex-1 py-3 rounded-full bg-[#1FB6E9] text-[#06121f] font-semibold text-sm">
              Book Online
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

export default PublicHeader;
